// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import type { ApprovalModel } from '../models/Approval'
import type { ConversationModel } from '../models/Conversation'
import type { MessageModel } from '../models/Message'
import type { MilestoneModel } from '../models/Milestone'
import type { ProjectModel } from '../models/Project'
import type { TaskModel } from '../models/Task'
import type { DiscussionMention, DiscussionModel } from '../types'
import { type AgentInboxCollections, type AgentInboxIdentity, getAgentInbox } from './agentInbox'
import {
  type AgentHierarchyInput,
  type AgentHierarchyRecord,
  getDescendants,
  getDirectReports,
} from './agentHierarchy'

export interface SubordinateReportOptions {
  managerSlug: string
  scope?: 'direct' | 'descendants'
  sinceDays?: number
  limit?: number
  now?: Date | string
  includeInbox?: boolean
  orgId?: string
  workspaceId?: string
}

export interface SubordinateControlPlaneReport {
  manager: { slug: string }
  scope: 'direct' | 'descendants'
  generatedAt: string
  windowStart: string
  rollup: {
    totalAgents: number
    activeTasks: number
    blockedTasks: number
    overdueTasks: number
    unreadInboxItems: number
    limitedAgents: number
  }
  agents: SubordinateAgentReport[]
}

export interface SubordinateAgentReport {
  identity: {
    slug: string
    teamMemberId?: string
    reportsTo?: string
    directReports: string[]
  }
  lifecycle: {
    provisioned: boolean
    containerEnabled: boolean
    containerStatus?: string
    heartbeatAt?: string
    heartbeatAgeSeconds?: number
    chatEndpoint?: string
    readiness: {
      rbac: 'ready' | 'missing'
      vault: 'ready' | 'missing'
      budget: 'ready' | 'missing'
      filesystem: 'ready' | 'missing'
      container: 'ready' | 'missing'
      health: 'ready' | 'missing'
    }
    readinessRefs: string[]
    blockerReasons: string[]
    remediation: string[]
  }
  work: {
    statusCounts: Record<string, number>
    activeTasks: number
    blockedTasks: number
    overdueTasks: number
    currentTaskIds: string[]
    taskLimit: number
    truncated: boolean
  }
  inbox: SubordinateInboxSummary
  blockers: SubordinateBlockerReference[]
}

export interface SubordinateInboxSummary {
  tasks: number
  mentions: number
  replies: number
  conversations: number
  approvals: number
  descriptionMentions: number
  totalUnread: number
}

export interface SubordinateBlockerReference {
  taskId?: string
  discussionId?: string
  title?: string
  status?: string
  updatedAt?: string
  discussionUpdatedAt?: string
}

export interface SubordinateReportCollections {
  teammembers?: TeamMemberReportSource
  records?: AgentRecordSource
  tasks?: OptionalFindCollection<TaskModel>
  discussions?: DiscussionReportCollection
  conversations?: OptionalFindCollection<ConversationModel>
  messages?: OptionalFindCollection<MessageModel>
  approvals?: OptionalFindCollection<ApprovalModel>
  milestones?: OptionalFindCollection<MilestoneModel>
  projects?: OptionalFindCollection<ProjectModel>
}

export type TeamMemberReportSource =
  | SubordinateTeamMemberRecord[]
  | {
      records?: SubordinateTeamMemberRecord[]
      find?: (query: ReportFindQuery) => Promise<SubordinateTeamMemberRecord[]>
      findByIds?: (ids: string[]) => Promise<SubordinateTeamMemberRecord[]>
    }

export type AgentRecordSource =
  | AgentHierarchyRecord[]
  | {
      records?: AgentHierarchyRecord[]
      find?: (query: ReportFindQuery) => Promise<AgentHierarchyRecord[]>
    }

export interface SubordinateTeamMemberRecord extends AgentHierarchyRecord {
  id: string
  orgId?: string
  workspaceId?: string
  userName: string
  slug?: string
  isAgent?: boolean
  lastHeartbeat?: string
  containerStatus?: string
}

interface OptionalFindCollection<T> {
  find(query: ReportFindQuery): Promise<T[]>
}

interface DiscussionReportCollection extends OptionalFindCollection<DiscussionModel> {
  findByIds?: (ids: string[]) => Promise<DiscussionModel[]>
}

interface ReportFindQuery {
  selector: Record<string, unknown>
  sort?: Array<Record<string, unknown>>
  limit?: number
}

const ACTIVE_TASK_STATUSES = new Set(['planned', 'todo', 'in-progress'])
const EMPTY_INBOX: SubordinateInboxSummary = {
  tasks: 0,
  mentions: 0,
  replies: 0,
  conversations: 0,
  approvals: 0,
  descriptionMentions: 0,
  totalUnread: 0,
}

export async function getSubordinateReport(
  collections: SubordinateReportCollections,
  options: SubordinateReportOptions
): Promise<SubordinateControlPlaneReport> {
  const scope = options.scope ?? 'direct'
  const limit = options.limit ?? 50
  const sinceDays = options.sinceDays ?? 7
  const now = toDate(options.now)
  const generatedAt = now.toISOString()
  const windowStart = new Date(now.getTime() - sinceDays * 86400_000).toISOString()

  if (!options.orgId || !options.workspaceId) {
    return emptyReport(options.managerSlug, scope, generatedAt, windowStart)
  }

  const allTeammembers = await collectTeamMembers(collections.teammembers, options)
  const allRecords = await collectAgentRecords(collections.records, options)
  const teammembers = filterRecordsByOptionTenancy(allTeammembers, options)
  const records = filterRecordsByOptionTenancy(allRecords, options)
  const hierarchyInput = hierarchyInputFrom(records, teammembers)

  const subordinateSlugs =
    scope === 'direct'
      ? getDirectReports(hierarchyInput, options.managerSlug)
      : getDescendants(hierarchyInput, options.managerSlug)

  const reports: SubordinateAgentReport[] = []
  for (const slug of subordinateSlugs) {
    const record = findRecordForSlug([...teammembers, ...records], slug)
    const identity = buildIdentity(record, slug, options)
    const taskPage = await findTasksForAgent(collections, identity, limit)
    const visibleTasks = taskPage.slice(0, limit)
    const discussions = await findBlockerDiscussions(collections, visibleTasks, identity, limit)
    const inbox = await buildInboxSummary(collections, identity, {
      includeInbox: options.includeInbox !== false,
      forceFallback: options.now !== undefined,
      sinceDays,
      limit,
      windowStart,
      windowEnd: generatedAt,
    })
    const reportsTo = findReportsTo(hierarchyInput, slug)

    reports.push({
      identity: {
        slug,
        ...(identity.teamMemberId ? { teamMemberId: identity.teamMemberId } : {}),
        ...(reportsTo ? { reportsTo } : {}),
        directReports: getHydratedDirectReports(hierarchyInput, slug),
      },
      lifecycle: buildLifecycle(record, records, now),
      work: summarizeWork(taskPage, now, limit),
      inbox,
      blockers: buildBlockers(visibleTasks, discussions),
    })
  }

  return {
    manager: { slug: normalizeSlug(options.managerSlug) },
    scope,
    generatedAt,
    windowStart,
    rollup: {
      totalAgents: reports.length,
      activeTasks: reports.reduce((sum, r) => sum + r.work.activeTasks, 0),
      blockedTasks: reports.reduce((sum, r) => sum + r.work.blockedTasks, 0),
      overdueTasks: reports.reduce((sum, r) => sum + r.work.overdueTasks, 0),
      unreadInboxItems: reports.reduce((sum, r) => sum + r.inbox.totalUnread, 0),
      limitedAgents: reports.filter(r => r.work.truncated).length,
    },
    agents: reports,
  }
}

async function collectTeamMembers(
  source: TeamMemberReportSource | undefined,
  options: Pick<SubordinateReportOptions, 'orgId' | 'workspaceId'>
): Promise<SubordinateTeamMemberRecord[]> {
  if (!source) return []
  if (Array.isArray(source)) return source
  if (source.records) return source.records
  if (source.find) return source.find({ selector: hierarchySourceSelector(options) })
  return []
}

async function collectAgentRecords(
  source: AgentRecordSource | undefined,
  options: Pick<SubordinateReportOptions, 'orgId' | 'workspaceId'>
): Promise<AgentHierarchyRecord[]> {
  if (!source) return []
  if (Array.isArray(source)) return source
  if (source.records) return source.records
  if (source.find) return source.find({ selector: hierarchySourceSelector(options) })
  return []
}

function hierarchySourceSelector(
  options: Pick<SubordinateReportOptions, 'orgId' | 'workspaceId'>
): Record<string, unknown> {
  return {
    _deleted: 0,
    ...(options.orgId ? { orgId: options.orgId } : {}),
    ...(options.workspaceId ? { workspaceId: options.workspaceId } : {}),
  }
}

function hierarchyInputFrom(
  records: AgentHierarchyRecord[],
  teammembers: SubordinateTeamMemberRecord[]
): AgentHierarchyInput {
  return {
    records,
    teammembers,
  }
}

function findRecordForSlug(
  records: Array<AgentHierarchyRecord | SubordinateTeamMemberRecord>,
  slug: string
): (AgentHierarchyRecord | SubordinateTeamMemberRecord) | undefined {
  const normalized = normalizeSlug(slug)
  return records.find(record => recordAliases(record).includes(normalized))
}

function filterRecordsByOptionTenancy<T extends AgentHierarchyRecord>(
  records: T[],
  options: Pick<SubordinateReportOptions, 'orgId' | 'workspaceId'>
): T[] {
  if (!options.orgId && !options.workspaceId) return records

  return records.filter(record => {
    const orgId = stringField(record, 'orgId')
    const workspaceId = stringField(record, 'workspaceId')
    const tenantId = stringField(record, 'tenantId')
    const hasTenantFields = Boolean(orgId || workspaceId || tenantId)

    if (!hasTenantFields) return true
    if (options.orgId && orgId !== options.orgId) return false
    if (options.workspaceId && workspaceId !== options.workspaceId) return false
    return true
  })
}

function buildIdentity(
  record: (AgentHierarchyRecord | SubordinateTeamMemberRecord) | undefined,
  slug: string,
  options: Pick<SubordinateReportOptions, 'orgId' | 'workspaceId'>
): {
  slug: string
  teamMemberId?: string
  orgId?: string
  workspaceId?: string
} {
  return {
    slug,
    ...(record?.id ? { teamMemberId: record.id } : {}),
    ...((options.orgId ?? stringField(record, 'orgId'))
      ? { orgId: options.orgId ?? stringField(record, 'orgId') }
      : {}),
    ...((options.workspaceId ?? stringField(record, 'workspaceId'))
      ? { workspaceId: options.workspaceId ?? stringField(record, 'workspaceId') }
      : {}),
  }
}

async function findTasksForAgent(
  collections: SubordinateReportCollections,
  identity: { teamMemberId?: string; orgId?: string; workspaceId?: string },
  limit: number
): Promise<TaskModel[]> {
  if (!collections.tasks || !identity.teamMemberId || !identity.orgId || !identity.workspaceId) {
    return []
  }

  const selector: Record<string, unknown> = {
    assigneeId: identity.teamMemberId,
    status: { $in: workTaskStatusSelectorValues() },
    completed: { $ne: true },
    archived: { $ne: true },
    _deleted: 0,
    orgId: identity.orgId,
    workspaceId: identity.workspaceId,
  }

  return collections.tasks.find({
    selector,
    sort: [{ priority: 'desc' }, { createdAt: 'desc' }],
    limit: limit + 1,
  })
}

async function findBlockerDiscussions(
  collections: SubordinateReportCollections,
  tasks: TaskModel[],
  identity: { orgId?: string; workspaceId?: string },
  limit: number
): Promise<DiscussionModel[]> {
  if (!collections.discussions || tasks.length === 0 || !identity.orgId || !identity.workspaceId) {
    return []
  }
  const blockedTaskIds = tasks.filter(isBlockedTask).map(t => t.id)
  if (blockedTaskIds.length === 0) return []

  const selector: Record<string, unknown> = {
    entityType: 'task',
    entityId: { $in: blockedTaskIds },
    _deleted: 0,
    orgId: identity.orgId,
    workspaceId: identity.workspaceId,
  }

  return collections.discussions.find({
    selector,
    sort: [{ updatedAt: 'desc' }],
    limit: Math.min(blockedTaskIds.length * 5, limit * 5),
  })
}

async function buildInboxSummary(
  collections: SubordinateReportCollections,
  identity: { slug: string; teamMemberId?: string; orgId?: string; workspaceId?: string },
  opts: {
    includeInbox: boolean
    forceFallback: boolean
    sinceDays: number
    limit: number
    windowStart: string
    windowEnd: string
  }
): Promise<SubordinateInboxSummary> {
  if (!opts.includeInbox || !identity.teamMemberId || !identity.orgId || !identity.workspaceId) {
    return { ...EMPTY_INBOX }
  }

  if (opts.forceFallback || !collections.tasks || !collections.discussions?.findByIds) {
    const [tasks, mentions, replies, conversations, approvals, descriptionMentions] =
      await Promise.all([
        countActiveInboxTasks(collections, identity, opts.limit),
        countDiscussionMentions(
          collections,
          identity,
          opts.windowStart,
          opts.windowEnd,
          opts.limit
        ),
        countDiscussionReplies(collections, identity, opts.windowStart, opts.windowEnd, opts.limit),
        countConversationUnread(
          collections,
          identity,
          opts.windowStart,
          opts.windowEnd,
          opts.limit
        ),
        countPendingApprovals(collections, identity, opts.limit),
        countDescriptionMentions(
          collections,
          identity,
          opts.windowStart,
          opts.windowEnd,
          opts.limit
        ),
      ])
    return withInboxTotal({
      ...EMPTY_INBOX,
      tasks,
      mentions,
      replies,
      conversations,
      approvals,
      descriptionMentions,
    })
  }

  const inboxCollections: AgentInboxCollections = {
    tasks: collections.tasks,
    discussions: {
      find: collections.discussions.find.bind(collections.discussions),
      findByIds: collections.discussions.findByIds.bind(collections.discussions),
    },
    ...(collections.conversations ? { conversations: collections.conversations } : {}),
    ...(collections.messages ? { messages: collections.messages } : {}),
    ...(collections.approvals ? { approvals: collections.approvals } : {}),
    ...(collections.milestones ? { milestones: collections.milestones } : {}),
    ...(collections.projects ? { projects: collections.projects } : {}),
    ...(hasFindByIds(collections.teammembers)
      ? {
          teammembers: {
            findByIds: collections.teammembers.findByIds.bind(collections.teammembers),
          },
        }
      : {}),
  }

  const inbox = await getAgentInbox(
    inboxCollections,
    {
      kind: 'agent',
      slug: identity.slug,
      teamMemberId: identity.teamMemberId,
      orgId: identity.orgId,
      workspaceId: identity.workspaceId,
    } satisfies AgentInboxIdentity,
    { sinceDays: opts.sinceDays, limit: opts.limit }
  )

  const conversations = inbox.conversations.reduce((sum, item) => sum + item.unreadCount, 0)
  return withInboxTotal({
    tasks: inbox.tasks.length,
    mentions: inbox.mentions.length,
    replies: inbox.replies.length,
    conversations,
    approvals: inbox.approvals.length,
    descriptionMentions: inbox.descriptionMentions.length,
    totalUnread: 0,
  })
}

async function countActiveInboxTasks(
  collections: SubordinateReportCollections,
  identity: { teamMemberId?: string; orgId?: string; workspaceId?: string },
  limit: number
): Promise<number> {
  if (!collections.tasks || !identity.teamMemberId || !identity.orgId || !identity.workspaceId) {
    return 0
  }

  const tasks = await collections.tasks.find({
    selector: {
      assigneeId: identity.teamMemberId,
      status: { $in: activeTaskStatusSelectorValues() },
      completed: { $ne: true },
      archived: { $ne: true },
      _deleted: 0,
      orgId: identity.orgId,
      workspaceId: identity.workspaceId,
    },
    sort: [{ priority: 'desc' }, { createdAt: 'desc' }],
    limit,
  })
  return tasks.length
}

async function countDiscussionMentions(
  collections: SubordinateReportCollections,
  identity: { slug: string; teamMemberId?: string; orgId?: string; workspaceId?: string },
  windowStart: string,
  windowEnd: string,
  limit: number
): Promise<number> {
  if (
    !collections.discussions ||
    !identity.teamMemberId ||
    !identity.orgId ||
    !identity.workspaceId
  ) {
    return 0
  }

  const discussions = await collections.discussions.find({
    selector: {
      createdAt: { $gte: windowStart, $lte: windowEnd },
      _deleted: 0,
      orgId: identity.orgId,
      workspaceId: identity.workspaceId,
    },
    sort: [{ createdAt: 'desc' }],
    limit: limit * 5,
  })

  return discussions
    .filter(discussion => mentionsMatchAgent(discussion.mentions, identity))
    .slice(0, limit).length
}

async function countDiscussionReplies(
  collections: SubordinateReportCollections,
  identity: { slug: string; teamMemberId?: string; orgId?: string; workspaceId?: string },
  windowStart: string,
  windowEnd: string,
  limit: number
): Promise<number> {
  if (
    !collections.discussions?.findByIds ||
    !identity.teamMemberId ||
    !identity.orgId ||
    !identity.workspaceId
  ) {
    return 0
  }

  const candidateReplies = await collections.discussions.find({
    selector: {
      parentId: { $exists: true, $ne: null },
      createdAt: { $gte: windowStart, $lte: windowEnd },
      userId: { $ne: identity.teamMemberId },
      _deleted: 0,
      orgId: identity.orgId,
      workspaceId: identity.workspaceId,
    },
    sort: [{ createdAt: 'desc' }],
    limit: limit * 5,
  })

  const actualReplies = candidateReplies.filter(
    reply => typeof reply.parentId === 'string' && reply.parentId.length > 0
  )
  const parentIds = Array.from(
    new Set(actualReplies.map(reply => reply.parentId).filter((id): id is string => Boolean(id)))
  )
  const parents = parentIds.length > 0 ? await collections.discussions.findByIds(parentIds) : []
  const parentById = new Map(parents.map(parent => [parent.id, parent]))

  const seen = new Set<string>()
  for (const reply of actualReplies) {
    const parent = reply.parentId ? parentById.get(reply.parentId) : undefined
    const isAuthoredThreadReply = parent?.userId === identity.teamMemberId
    const isMentionedReply = mentionsMatchAgent(reply.mentions, identity)
    if (!isAuthoredThreadReply && !isMentionedReply) continue
    seen.add(reply.id)
    if (seen.size >= limit) break
  }

  return seen.size
}

async function countConversationUnread(
  collections: SubordinateReportCollections,
  identity: { slug: string; teamMemberId?: string; orgId?: string; workspaceId?: string },
  windowStart: string,
  windowEnd: string,
  limit: number
): Promise<number> {
  // Agent-chat conversations + their messages are org-scoped (see
  // collection-scope.ts: `conversations: 'org'` and the per-message
  // workspaceId-injection exemption for agent-chat in rest-api.ts).
  // Scoping by `identity.workspaceId` here would reproduce the inbox
  // visibility bug from 2026-05-23 — managers would silently see 0
  // unread for cross-workspace DMs their subordinates participate in.
  if (
    !collections.conversations ||
    !collections.messages ||
    !identity.teamMemberId ||
    !identity.orgId
  ) {
    return 0
  }

  const conversations = await collections.conversations.find({
    selector: {
      'metadata.kind': 'agent-chat',
      'metadata.participantIds': { $in: [identity.teamMemberId] },
      _deleted: 0,
      orgId: identity.orgId,
    },
    sort: [{ updatedAt: 'desc' }],
    limit: limit * 2,
  })
  if (conversations.length === 0) return 0

  const messages = await collections.messages.find({
    selector: {
      conversationId: { $in: conversations.map(conversation => conversation.id) },
      createdAt: { $gte: windowStart, $lte: windowEnd },
      userId: { $ne: identity.teamMemberId },
      _deleted: 0,
      orgId: identity.orgId,
    },
    sort: [{ createdAt: 'desc' }],
    limit: limit * 200,
  })

  const unreadByConversationId = new Map<string, MessageModel[]>()
  for (const message of messages) {
    const unread = unreadByConversationId.get(message.conversationId) ?? []
    unread.push(message)
    unreadByConversationId.set(message.conversationId, unread)
  }

  let total = 0
  for (const conversation of conversations) {
    const unread = unreadByConversationId.get(conversation.id) ?? []
    if (unread.length === 0) continue

    const mutedBy = readStringArray(conversation.metadata?.mutedBy)
    const isMuted = mutedBy.includes(identity.teamMemberId)
    const hasUnreadMention = unread.some(message =>
      mentionsMatchAgent(message.mentions as DiscussionMention[] | undefined, identity)
    )
    if (isMuted && !hasUnreadMention) continue

    total += unread.length
  }

  return total
}

async function countPendingApprovals(
  collections: SubordinateReportCollections,
  identity: { teamMemberId?: string; orgId?: string; workspaceId?: string },
  limit: number
): Promise<number> {
  if (
    !collections.approvals ||
    !identity.teamMemberId ||
    !identity.orgId ||
    !identity.workspaceId
  ) {
    return 0
  }

  const approvals = await collections.approvals.find({
    selector: {
      approverId: identity.teamMemberId,
      _deleted: 0,
      orgId: identity.orgId,
      workspaceId: identity.workspaceId,
    },
    sort: [{ createdAt: 'desc' }],
    limit: limit * 5,
  })
  return approvals.filter(approval => approval.status.toLowerCase() === 'pending').slice(0, limit)
    .length
}

async function countDescriptionMentions(
  collections: SubordinateReportCollections,
  identity: { slug: string; teamMemberId?: string; orgId?: string; workspaceId?: string },
  windowStart: string,
  windowEnd: string,
  limit: number
): Promise<number> {
  if (!identity.teamMemberId || !identity.orgId || !identity.workspaceId) return 0

  const [tasks, milestones, projects] = await Promise.all([
    findDescriptionMentionRows(collections.tasks, identity, windowStart, windowEnd, limit),
    findDescriptionMentionRows(collections.milestones, identity, windowStart, windowEnd, limit),
    findDescriptionMentionRows(collections.projects, identity, windowStart, windowEnd, limit),
  ])

  return [...tasks, ...milestones, ...projects]
    .filter(row => mentionsMatchAgent(row.mentions as DiscussionMention[] | undefined, identity))
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .slice(0, limit).length
}

async function findDescriptionMentionRows<T extends { updatedAt: string; mentions?: unknown[] }>(
  collection: OptionalFindCollection<T> | undefined,
  identity: { orgId?: string; workspaceId?: string },
  windowStart: string,
  windowEnd: string,
  limit: number
): Promise<T[]> {
  if (!collection || !identity.orgId || !identity.workspaceId) return []

  return collection.find({
    selector: {
      _deleted: 0,
      orgId: identity.orgId,
      workspaceId: identity.workspaceId,
      updatedAt: { $gte: windowStart, $lte: windowEnd },
    },
    sort: [{ updatedAt: 'desc' }],
    limit: limit * 5,
  })
}

function withInboxTotal(summary: SubordinateInboxSummary): SubordinateInboxSummary {
  return {
    ...summary,
    totalUnread:
      summary.tasks +
      summary.mentions +
      summary.replies +
      summary.conversations +
      summary.approvals +
      summary.descriptionMentions,
  }
}

function mentionsMatchAgent(
  mentions: DiscussionMention[] | undefined,
  identity: { slug: string; teamMemberId?: string }
): boolean {
  if (!mentions || mentions.length === 0) return false
  const slugLower = identity.slug.toLowerCase()
  return mentions.some(
    mention =>
      mention.teamMemberId === identity.teamMemberId ||
      (typeof mention.identifier === 'string' && mention.identifier.toLowerCase() === slugLower)
  )
}

function readStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : []
}

function summarizeWork(
  tasks: TaskModel[],
  now: Date,
  taskLimit: number
): SubordinateAgentReport['work'] {
  const visibleTasks = tasks.slice(0, taskLimit)
  const statusCounts: Record<string, number> = {}
  const currentTaskIds: string[] = []
  let activeTasks = 0
  let blockedTasks = 0
  let overdueTasks = 0

  for (const task of visibleTasks) {
    statusCounts[task.status] = (statusCounts[task.status] ?? 0) + 1
    if (isActiveTask(task)) {
      activeTasks += 1
      currentTaskIds.push(task.id)
      if (isOverdueTask(task, now)) overdueTasks += 1
    }
    if (isBlockedTask(task)) blockedTasks += 1
  }

  return {
    statusCounts,
    activeTasks,
    blockedTasks,
    overdueTasks,
    currentTaskIds,
    taskLimit,
    truncated: tasks.length > taskLimit,
  }
}

function buildBlockers(
  tasks: TaskModel[],
  discussions: DiscussionModel[]
): SubordinateBlockerReference[] {
  const discussionsByTask = new Map<string, DiscussionModel>()
  for (const discussion of discussions) {
    if (discussion.entityType !== 'task') continue
    const existing = discussionsByTask.get(discussion.entityId)
    if (!existing || discussion.updatedAt > existing.updatedAt) {
      discussionsByTask.set(discussion.entityId, discussion)
    }
  }

  return tasks.filter(isBlockedTask).map(task => {
    const discussion = discussionsByTask.get(task.id)
    return {
      taskId: task.id,
      ...(discussion ? { discussionId: discussion.id } : {}),
      title: task.title,
      status: task.status,
      updatedAt: task.updatedAt,
      ...(discussion ? { discussionUpdatedAt: discussion.updatedAt } : {}),
    }
  })
}

function buildLifecycle(
  record: (AgentHierarchyRecord | SubordinateTeamMemberRecord) | undefined,
  records: AgentHierarchyRecord[],
  now: Date
) {
  const metadata = record?.metadata ?? {}
  const extendedMetadata = record?.extended?.agent?.metadata ?? {}
  const slug = record ? recordAliases(record)[0] : undefined
  const teamMemberId = stringField(record, 'id')
  const readinessRecords = slug
    ? records.filter(row => readinessRecordMatches(row, slug, teamMemberId))
    : []
  const readiness = {
    rbac:
      hasAnySchema(readinessRecords, [
        'openclaw.permission-grant',
        'openclaw.service-account-grant',
      ]) &&
      hasAnySchema(readinessRecords, [
        'openclaw.role-membership',
        'openclaw.service-account-grant',
      ]) &&
      hasAnySchema(readinessRecords, ['openclaw.acl'])
        ? 'ready'
        : 'missing',
    vault: hasAllSchemas(readinessRecords, [
      'openclaw.vault',
      'openclaw.vault-membership',
      'openclaw.vault-item-template',
      'openclaw.service-key',
      'openclaw.delegated-secret',
    ])
      ? 'ready'
      : 'missing',
    budget:
      (hasAnySchema(readinessRecords, ['openclaw.budget-template']) ||
        hasAnySchema(records, ['openclaw.budget-template'])) &&
      hasAllSchemas(readinessRecords, ['openclaw.budget-assignment', 'openclaw.budget-readiness'])
        ? 'ready'
        : 'missing',
    filesystem: hasAnySchema(readinessRecords, ['openclaw.filesystem']) ? 'ready' : 'missing',
    container: 'missing',
    health: 'missing',
  } as const
  const containerEnabled =
    booleanValue(metadata.containerEnabled) ?? booleanValue(extendedMetadata.containerEnabled)
  const containerStatus =
    stringField(record, 'containerStatus') ?? stringFrom(metadata.containerStatus)
  const heartbeatAt =
    stringField(record, 'lastHeartbeat') ??
    stringFrom(metadata.lastHeartbeat) ??
    stringFrom(metadata.heartbeatAt)
  const heartbeatAgeSeconds = heartbeatAt
    ? calculateHeartbeatAgeSeconds(heartbeatAt, now)
    : undefined
  const chatEndpoint =
    stringFrom(metadata.chatEndpoint) ?? stringFrom(extendedMetadata.chatEndpoint)
  const containerReady = ['running', 'healthy'].includes((containerStatus ?? '').toLowerCase())
  const healthReady = heartbeatAgeSeconds !== undefined && containerReady
  const fullReadiness = {
    ...readiness,
    container: containerReady ? 'ready' : readiness.container,
    health: healthReady ? 'ready' : 'missing',
  } as const
  const blockerReasons = lifecycleBlockers(fullReadiness, heartbeatAt)

  return {
    provisioned:
      booleanValue(metadata.provisioned) ??
      (fullReadiness.rbac === 'ready' &&
        fullReadiness.vault === 'ready' &&
        fullReadiness.budget === 'ready' &&
        fullReadiness.filesystem === 'ready'),
    containerEnabled: containerEnabled ?? false,
    ...(containerStatus ? { containerStatus } : {}),
    ...(heartbeatAt ? { heartbeatAt } : {}),
    ...(heartbeatAgeSeconds !== undefined ? { heartbeatAgeSeconds } : {}),
    ...(chatEndpoint ? { chatEndpoint } : {}),
    readiness: fullReadiness,
    readinessRefs: readinessRecords.map(recordRef),
    blockerReasons,
    remediation: remediationHints(blockerReasons),
  }
}

function calculateHeartbeatAgeSeconds(heartbeatAt: string, now: Date): number | undefined {
  const heartbeatMs = Date.parse(heartbeatAt)
  if (!Number.isFinite(heartbeatMs)) return undefined
  return Math.max(0, Math.floor((now.getTime() - heartbeatMs) / 1000))
}

function readinessRecordMatches(
  record: AgentHierarchyRecord,
  slug: string,
  teamMemberId: string | undefined
): boolean {
  const data = isRecord(record.data) ? record.data : {}
  const metadata = record.metadata ?? {}
  const values = [
    record.id,
    record.name,
    record.title,
    stringFrom(record.parentId),
    stringFrom(record.agentSlug),
    stringFrom(record.teamMemberId),
    stringFrom(data.agentSlug),
    stringFrom(data.teamMemberId),
    stringFrom(metadata.agentSlug),
    stringFrom(metadata.teamMemberId),
  ].filter((value): value is string => Boolean(value))
  const ids = new Set([slug, `team_${slug}`, `agent:${slug}`])
  if (teamMemberId) ids.add(teamMemberId)
  return values.some(value => {
    const normalized = normalizeSlug(value)
    return ids.has(value) || normalized === slug || hasExplicitSlugSegment(value, slug)
  })
}

function hasAnySchema(records: AgentHierarchyRecord[], schemaIds: string[]): boolean {
  const allowed = new Set(schemaIds)
  return records.some(row => allowed.has(String(row.schemaId ?? '')))
}

function hasAllSchemas(records: AgentHierarchyRecord[], schemaIds: string[]): boolean {
  return schemaIds.every(schemaId => hasAnySchema(records, [schemaId]))
}

function lifecycleBlockers(
  readiness: SubordinateAgentReport['lifecycle']['readiness'],
  heartbeatAt: string | undefined
): string[] {
  const blockers: string[] = []
  if (readiness.rbac !== 'ready') blockers.push('missing RBAC grants')
  if (readiness.vault !== 'ready') blockers.push('missing vault materialization')
  if (readiness.budget !== 'ready') blockers.push('missing budget assignment')
  if (readiness.filesystem !== 'ready') blockers.push('missing filesystem readiness')
  if (readiness.container !== 'ready') blockers.push('container not running')
  if (!heartbeatAt) blockers.push('missing heartbeat')
  return blockers
}

function remediationHints(blockers: string[]): string[] {
  const hints = new Map<string, string>([
    ['missing RBAC grants', 'Run subordinate provisioning with RBAC repair enabled.'],
    ['missing vault materialization', 'Run subordinate provisioning vault materialization.'],
    ['missing budget assignment', 'Create or repair subordinate budget assignment.'],
    ['missing filesystem readiness', 'Repair subordinate filesystem readiness.'],
    ['container not running', 'Start or reconcile the subordinate container.'],
    ['missing heartbeat', 'Check subordinate heartbeat and container telemetry.'],
  ])
  return blockers.map(blocker => hints.get(blocker)).filter((hint): hint is string => Boolean(hint))
}

function recordRef(record: AgentHierarchyRecord): string {
  return `records:${String(record.id ?? record.title ?? 'unknown')}`
}

function hasExplicitSlugSegment(value: string, slug: string): boolean {
  return value
    .toLowerCase()
    .split(/[/:]/)
    .map(segment => normalizeSlug(segment))
    .some(segment => segment === slug)
}

function findReportsTo(input: AgentHierarchyInput, slug: string): string | undefined {
  const records = collectHierarchyRecords(input)
  for (const record of records) {
    const parentSlug = recordAliases(record)[0]
    if (!parentSlug) continue
    if (getDirectReports(input, parentSlug).includes(slug)) return parentSlug
  }
  return undefined
}

function getHydratedDirectReports(input: AgentHierarchyInput, slug: string): string[] {
  try {
    return getDirectReports(input, slug)
  } catch (error) {
    if (error instanceof Error && /unknown manager slug/i.test(error.message)) {
      return []
    }
    throw error
  }
}

function collectHierarchyRecords(input: AgentHierarchyInput): AgentHierarchyRecord[] {
  if (Array.isArray(input)) return input
  return [...(input.records ?? []), ...recordsFromHierarchySource(input.teammembers)]
}

function recordsFromHierarchySource(
  source: AgentHierarchyRecord[] | { records?: AgentHierarchyRecord[] } | undefined
): AgentHierarchyRecord[] {
  if (!source) return []
  if (Array.isArray(source)) return source
  return source.records ?? []
}

function emptyReport(
  managerSlug: string,
  scope: 'direct' | 'descendants',
  generatedAt: string,
  windowStart: string
): SubordinateControlPlaneReport {
  return {
    manager: { slug: normalizeSlug(managerSlug) },
    scope,
    generatedAt,
    windowStart,
    rollup: {
      totalAgents: 0,
      activeTasks: 0,
      blockedTasks: 0,
      overdueTasks: 0,
      unreadInboxItems: 0,
      limitedAgents: 0,
    },
    agents: [],
  }
}

function isActiveTask(task: TaskModel): boolean {
  return (
    ACTIVE_TASK_STATUSES.has(normalizeTaskStatus(task.status)) &&
    task.completed !== true &&
    task.archived !== true
  )
}

function isBlockedTask(task: TaskModel): boolean {
  const metadata = task.metadata ?? {}
  return (
    normalizeTaskStatus(task.status) === 'blocked' ||
    booleanValue(metadata.blocked) === true ||
    Array.isArray(metadata.blockers) ||
    typeof metadata.blocker === 'string'
  )
}

function activeTaskStatusSelectorValues(): string[] {
  return ['Planned', 'planned', 'In Progress', 'in_progress', 'in-progress', 'todo']
}

function workTaskStatusSelectorValues(): string[] {
  return [...activeTaskStatusSelectorValues(), 'Blocked', 'blocked']
}

function normalizeTaskStatus(status: string | undefined): string {
  return (status ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-')
}

function isOverdueTask(task: TaskModel, now: Date): boolean {
  if (!isActiveTask(task) || !task.dueAt) return false
  const dueAt = Date.parse(task.dueAt)
  return Number.isFinite(dueAt) && dueAt < now.getTime()
}

function toDate(value: Date | string | undefined): Date {
  if (value instanceof Date) return value
  if (typeof value === 'string') return new Date(value)
  return new Date()
}

function hasFindByIds(
  source: TeamMemberReportSource | undefined
): source is { findByIds: (ids: string[]) => Promise<SubordinateTeamMemberRecord[]> } {
  return (
    typeof source === 'object' &&
    source !== null &&
    !Array.isArray(source) &&
    typeof source.findByIds === 'function'
  )
}

function recordAliases(record: AgentHierarchyRecord): string[] {
  const metadata = record.metadata ?? {}
  const extendedAgent = record.extended?.agent
  const extendedMetadata = extendedAgent?.metadata ?? {}
  return [
    stringFrom(record.slug),
    stringFrom(record.name),
    stringFrom(record.userName),
    stringFrom(metadata.slug),
    stringFrom(metadata.name),
    stringFrom(metadata.orgRole),
    stringFrom(extendedAgent?.name),
    stringFrom(extendedMetadata.orgRole),
  ]
    .filter((value): value is string => Boolean(value))
    .map(normalizeSlug)
}

function normalizeSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/\b(?:of|the|and)\b/g, '')
    .replace(/[()]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function stringField(record: unknown, key: string): string | undefined {
  if (!isRecord(record)) return undefined
  return stringFrom(record[key])
}

function stringFrom(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined
}

function booleanValue(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
